export type Permission = "admin" | "catalogs" | "products";
export type PermissionType = { [key in Permission]: string };

export const FreePlan = "free";

export const STRIPE_GATEWAY = "Stripe";
export const SHOPIFY_GATEWAY = "Shopify";

export type GatewayType = typeof STRIPE_GATEWAY | typeof SHOPIFY_GATEWAY;

export type AppPlatform = "cm1" | "cm2";

export type AccountSummary = {
  //lastRefresh: string;
  //lastImageRefresh: string;
  itemsCount: number;
  categoriesCount: number;
  imagesCount: number;
  imagesSize: number;
  imageSizeMb: number;
  catalogsCount: number;
  templatesCount: number;
  usersCount: number;
  activeUsersCount: number;
  publishedCatalogs: number;
  sessions: number;
  showrooms: number;
  showroomPrivacy: boolean;
  newOrders: number;
  concierge?: ConciergeStatus;
  prevSessionDays: number;
  profile: {
    accountId: number;
    name: string;
    value: string;
    number: number;
    updated: string;
  }[];
  // imageSizeFormatted: string;
  // spaceLimit: string;
  // itemsLimit: string;
  // catalogsLimit: string;
  // usersLimit: string;
  features: {
    showroom: boolean;
    orders: boolean;
    collections: boolean;
    templates: boolean;
    layouts: boolean;
  };
  /** Account has a live Shopify store token. Connection state, not a feature toggle. */
  shopifyConnected: boolean;
  /** The account has a WooCommerce store address saved. */
  wooCommerceConnected?: boolean;
  metaProfile: {
    item1: string;
    item2: string;
  }[];
};

/** Concierge AI credits summary */
export type ConciergeCreditsSummary = {
  /** Total available credits in dollars (monthly + topUp) */
  available: number;
  /** Monthly allocation in dollars */
  monthlyIncluded: number;
  /** Remaining monthly credits in dollars */
  monthly: number;
  /** Top-up credits in dollars */
  topUp: number;
  /** Next reset date (null if not initialized) */
  resetAt?: string;
};

/** Concierge AI status for NavBar badge and Dashboard display */
export type ConciergeStatus = {
  /** Total sessions created */
  totalSessions: number;
  /** Sessions updated in last 7 days */
  activeSessionsLast7Days: number;
  /** Has an active session with uncommitted pending changes */
  hasUncommittedSession: boolean;
  /** User has never created a concierge session */
  isNew: boolean;
  /** Credit balance summary */
  credits?: ConciergeCreditsSummary;
};

export type AccountRef = {
  id: number;
  namespace: string;
  imageNamespace: string;
  businessName: string;
  created: string;
  isAdmin: boolean;
  permissions: PermissionType;
  category: string;
  planCode: string;
  planName: string;
  priceDescription: string;
  brandColor?: string;
  logo: string;
  summary: AccountSummary;
  limits: AccountLimits;
  config: AccountConfig;
  apiKey: string;
  age?: string;
  isActivated: boolean;
  isPaying: boolean;
  isProPlan: boolean;
  gateway?: GatewayType;
  country?: string;
  monthlyPrice?: number;
  subscriptionStatus?: string;
  subscriptionChanged?: string;
  abTestGroup?: AppPlatform;
  errorStatus?: string;
  support?: "premier" | "standard" | "basic";
  shop?: string;
  signUpSource?: string;
  urlReferrer?: string;
  enableIntlFonts?: boolean;
};

export type User = {
  id: number;
  email: string;
  name: string;
  accounts: AccountRef[];
  hmac: string;
  created: string;
  role: string;
  firstName?: string;
  lastName?: string;
};

export type AccountLimits = {
  variants: number;
  /** Pages per catalog. `-1` means unlimited - never render it raw. */
  pageLimit: number;
  /**
   * Maximum PDF-import upload in MB, from the server's own gate
   * (`PdfImportService.MaxUploadBytes`). The only number the UI may quote: the form used to
   * hardcode 50 while the info card computed `min(100, storage remaining)` and showed 100.
   */
  pdfUploadMb: number;
  items?: number;
  catalogs?: number;
  users?: number;
  space?: number;
  stores?: number;
};

export type AccountConfig = {
  lang: string;
  imageStorageUrl: string;
  imageCdnUrl: string;
  culture: string;
  catalogPath: string;
  apiV1Url: string;
  pdfApiUrl: string;
  /**
   * S72 - the `?v=` cache token for this account's image URLs, or undefined when no image has
   * ever been overwritten. Moves whenever an image is replaced or deleted, which gives every
   * image a URL no browser, CDN or PDF renderer has seen before.
   *
   * This replaced `?Date.now()`. That buster made the app always fresh while the published
   * catalog served a cached copy - which is exactly why stale images went unnoticed for months.
   */
  imageVersion?: string;
};

export type AppSession = {
  token: string;
  succeeded?: boolean;
  user?: User;
  account?: AccountRef;
  message?: string;
  provider?: string;
  requiresSignup?: boolean;
  sessionId: string;
};

export type AccountInfo = {
  summary: AccountSummary;
};

export type DecodedToken = {
  provider: string;
  su: string;
  /** S71 — support console claim (roles `crm` or `siteadmin`). Narrower than `su`. */
  sup: string;
  /** S71 — impersonate claim (roles `login` or `siteadmin`). Gates /admin-login. Not `im`. */
  lgn: string;
  im: string;
  exp: number;
  sessionId: string;
};

export type ExternalUser = {
  email: string;
  firstName: string;
  lastName: string;
  provider: string;
  emailExists: boolean;
};

export type AccountUser = {
  id?: number;
  userId?: number;
  user?: User;
  email?: string;
  modified?: string;
  created?: string;
  activated?: string;
  status?: "Active" | "Pending";
  role?: UserRole;
  name?: string;
  permissions?: string[];
  inviteLink?: string;
};

export type UserRoleAccess = "Full" | "Restricted";

export type UserRole = {
  access: UserRoleAccess;
  permissions: { [key: string]: boolean };
};
